import React, { Component } from 'react';
import { Editor as SlateEditor } from 'slate-react';
import { Block } from 'slate';
import PropTypes from 'prop-types';
import DeepTable from 'slate-deep-table';
import PasteLinkify from 'slate-paste-linkify';
import { HotKeys } from 'react-hotkeys';
import cx from 'classnames';
import Toolbar from './Toolbar/Toolbar';
import renderMark from './renderMark';
import renderNode from './renderNode';
import { NODES, LINK_TARGETS } from '../../constants';
import { keyMappings, handlerMappings } from './hotkeyMapping';
import { EventEmitter } from '../../utils';
import './editor.scss';

const plugins = [
  DeepTable({
    typeTable: NODES.TABLE,
    typeRow: NODES.TABLE_ROW,
    typeCell: NODES.TABLE_CELL,
    typeContent: NODES.PARAGRAPH,
  }),
  PasteLinkify(),
];
const commands = {
  wrapLink(change, href) {
    change.wrapInline({
      type: NODES.LINK,
      data: { href, target: LINK_TARGETS.SAME_WINDOW },
    });
  },
  unwrapLink(change) {
    change.unwrapInline(NODES.LINK);
  },
};

const queries = {
  isLinkActive(editor, value) {
    const { inlines } = value;
    const active = inlines.some(i => i.type === NODES.LINK);
    return active;
  },
};

const schema = {
  document: {
    last: { type: NODES.PARAGRAPH },
    normalize: (editor, { code, node }) => {
      // eslint-disable-next-line default-case
      switch (code) {
        case 'last_child_type_invalid': {
          const paragraph = Block.create(NODES.PARAGRAPH);
          return editor.insertNodeByKey(node.key, node.nodes.size, paragraph);
        }
      }
    },
  },
  inlines: {
    [NODES.TRANSLATION]: {
      isVoid: true,
    },
    [NODES.SNIPPET_BUTTON]: {
      isVoid: true,
    },
  },
  blocks: {
    [NODES.IMAGE]: {
      isVoid: true,
    },
    [NODES.VIDEO]: {
      isVoid: true,
    },
    [NODES.SNIPPET]: {
      isVoid: true,
    },
  },
};

class Editor extends Component {
  static propTypes = {
    value: PropTypes.object,
    className: PropTypes.string,
    onChange: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      fullscreen: false,
    };

    this.editorReference = this.editorReference.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.undo = this.undo.bind(this);
    this.redo = this.redo.bind(this);
    this.toggleFullScreen = this.toggleFullScreen.bind(this);
  }

  componentDidMount() {
    EventEmitter.listen('toggleFullscreen', () => {
      this.toggleFullScreen();
    });
  }

  toggleFullScreen() {
    this.setState(prevState => ({ fullscreen: !prevState.fullscreen }));
  }

  undo() {
    this.state.editor.undo();
  }

  redo() {
    this.state.editor.redo();
  }

  editorReference(editor) {
    this.setState({ editor }, () => {
      EventEmitter.listen('mark-hotkey', type => {
        editor.toggleMark(type);
      });
    });
  }

  onKeyDown(event, editor, next) {
    const { key, shiftKey } = event;

    if (key === 'Enter' && shiftKey) {
      return editor.insertText('\n');
    }

    return next();
  }

  render() {
    return (
      <div
        className={cx('editor', this.props.className, {
          fullscreen: this.state.fullscreen,
        })}>
        <Toolbar
          {...this.props}
          editor={this.state.editor}
          value={this.props.value}
          undo={this.undo}
          redo={this.redo}
        />
        <HotKeys keyMap={keyMappings} handlers={handlerMappings}>
          <SlateEditor
            className="editor__input"
            plugins={plugins}
            commands={commands}
            queries={queries}
            value={this.props.value}
            onChange={this.props.onChange}
            onKeyDown={this.onKeyDown}
            renderMark={renderMark}
            renderNode={renderNode}
            schema={schema}
            ref={this.editorReference}
          />
        </HotKeys>
      </div>
    );
  }
}

export default  Editor;
